import React from "react";
import { Button, Card, CardBody, Col, FormGroup, Input, Label, Row } from "reactstrap";
import SectionHeader from "@dhn/components/molecules/section-header";
import SelectableOption, {
  SelectableOptionGroup,
} from "@dhn/components/molecules/selectable-option";
import PhoneNumberInput from "../../../@dhn/components/molecules/phone-number-input";
import CommercialFields from "./CommercialFields";
import LocationFields from "./LocationFields";
import ValidationFeedback from "@dhn/components/molecules/validation-feedback";

const LegalPersonFields = ({
  form,
  isSubmitting,
  isValidatingIdentity,
  provinces,
  businessMunicipalities,
  businessSectors,
  legalUsername,
  inlineErrors,
  identityValidation,
  representativeValidation,
  onChange,
  handleValidateRepresentativeIdentity,
  onRepresentativeDocumentNumberChange,
  showInlineError,
  representativeValidateButtonIcon,
  representativeValidateButtonLabel,
  isRepresentativeFieldLocked,
}) => (
  <Card className="fade-in-up mb-3">
    <CardBody>
      <SectionHeader
        icon="fa-building-o"
        title={`En caso de ser Persona Moral - ${form.isMedical ? "Médico" : "Usuario"}`}
      />
      <CommercialFields
        form={form}
        inlineErrors={inlineErrors}
        identityValidation={identityValidation}
        onChange={onChange}
        showInlineError={showInlineError}
      />

      <Row form>
        <Col md={4}>
          <FormGroup>
            <Label>
              Celular <span className="required-mark">*</span>
            </Label>
            <PhoneNumberInput
              value={form.businessCellphone}
              onChange={(value) =>
                onChange("businessCellphone", value)
              }
              placeholder="campo obligatorio"
            />
            {showInlineError("businessCellphone")}
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Teléfono</Label>
            <PhoneNumberInput
              value={form.businessPhone}
              onChange={(value) =>
                onChange("businessPhone", value)
              }
              placeholder="Ingrese telefono"
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>
              Correo electrónico <span className="required-mark">*</span>
            </Label>
            <Input
              name="business_email"
              autoComplete="section-business email"
              type="email"
              invalid={Boolean(inlineErrors.businessEmail)}
              value={form.businessEmail}
              onChange={(event) =>
                onChange("businessEmail", event.target.value)
              }
              placeholder="campo obligatorio"
            />
            {showInlineError("businessEmail")}
          </FormGroup>
        </Col>
      </Row>

      <Row form>
        <Col>
          <FormGroup>
            <Label>
              Dirección <span className="required-mark">*</span>
            </Label>
            <Input
              name="business_address"
              autoComplete="section-business street-address"
              invalid={Boolean(inlineErrors.businessAddress)}
              value={form.businessAddress}
              onChange={(event) =>
                onChange("businessAddress", event.target.value)
              }
              placeholder="campo obligatorio"
            />
            {showInlineError("businessAddress")}
          </FormGroup>
        </Col>
      </Row>
      <LocationFields
        prefix="business"
        form={form}
        provinces={provinces}
        municipalities={businessMunicipalities}
        sectors={businessSectors}
        inlineErrors={inlineErrors}
        onChange={onChange}
        showInlineError={showInlineError}
        disabled={isSubmitting}
      />

      <SectionHeader
        className="mt-4"
        icon="fa-user-circle-o"
        title="Representante Legal"
        variant="secondary"
      />
      <Row form>
        <Col md={6}>
          <Label className="d-block">Tipo de Documento</Label>
          <SelectableOptionGroup columns={2}>
            <SelectableOption
              key="rep-document-type-cedula"
              icon="fa-id-card-o"
              title="Cédula"
              inputType="radio"
              inputName="prereg-rep-document-type"
              checked={form.repDocumentType === "CEDULA"}
              onChange={() =>
                onChange("repDocumentType", "CEDULA")
              }
            />
            <SelectableOption
              key="rep-document-type-pasaporte"
              icon="fa-globe"
              title="Pasaporte"
              inputType="radio"
              inputName="prereg-rep-document-type"
              checked={form.repDocumentType === "PASAPORTE"}
              onChange={() =>
                onChange("repDocumentType", "PASAPORTE")
              }
            />
          </SelectableOptionGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>
              No. Documento <span className="required-mark">*</span>
            </Label>
            <div className="document-lookup-row">
              <div className="document-lookup-input">
                <Input
                  name="representative_document_number"
                  autoComplete="section-representative off"
                  invalid={Boolean(inlineErrors.repDocumentNumber)}
                  value={form.repDocumentNumber}
                  inputMode={
                    form.repDocumentType === "PASAPORTE"
                      ? "text"
                      : "numeric"
                  }
                  maxLength={
                    form.repDocumentType === "CEDULA"
                      ? 13
                      : undefined
                  }
                  onChange={(event) =>
                    onRepresentativeDocumentNumberChange(
                      event.target.value,
                    )
                  }
                  placeholder="campo obligatorio"
                />
              </div>
              <Button
                type="button"
                color="primary"
                className="btn-validate py-2"
                disabled={
                  isSubmitting ||
                  isValidatingIdentity ||
                  !form.repDocumentNumber
                }
                onClick={handleValidateRepresentativeIdentity}
              >
                <i className={`fa ${representativeValidateButtonIcon} mr-1`} aria-hidden="true"></i>
                {representativeValidateButtonLabel}
              </Button>
            </div>
            {showInlineError("repDocumentNumber")}
            {representativeValidation.status !== "idle" && (
              <ValidationFeedback
                className="mt-2"
                status={representativeValidation.status}
              >
                {representativeValidation.message}
              </ValidationFeedback>
            )}
          </FormGroup>
        </Col>
      </Row>

      {representativeValidation.status === "validated" ? (
        <>
          <Row form>
            <Col md={3}>
              <FormGroup>
                <Label>
                  Primer Nombre <span className="required-mark">*</span>
                </Label>
                <Input
                  name="representative_first_name"
                  autoComplete="section-representative name"
                  invalid={Boolean(inlineErrors.repFirstName)}
                  value={form.repFirstName}
                  disabled={isRepresentativeFieldLocked("repFirstName")}
                  onChange={(event) =>
                    onChange("repFirstName", event.target.value)
                  }
                  placeholder="Ingrese primer nombre"
                />
                {showInlineError("repFirstName")}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Segundo Nombre</Label>
                <Input
                  name="representative_middle_name"
                  autoComplete="section-representative additional-name"
                  value={form.repMiddleName}
                  disabled={isRepresentativeFieldLocked("repMiddleName")}
                  onChange={(event) =>
                    onChange("repMiddleName", event.target.value)
                  }
                  placeholder="Opcional"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>
                  Primer Apellido <span className="required-mark">*</span>
                </Label>
                <Input
                  name="representative_last_name"
                  autoComplete="section-representative family-name"
                  invalid={Boolean(inlineErrors.repLastName)}
                  value={form.repLastName}
                  disabled={isRepresentativeFieldLocked("repLastName")}
                  onChange={(event) =>
                    onChange("repLastName", event.target.value)
                  }
                  placeholder="Ingrese primer apellido"
                />
                {showInlineError("repLastName")}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Segundo Apellido</Label>
                <Input
                  name="representative_last_name2"
                  autoComplete="new-password"
                  value={form.repLastName2}
                  disabled={isRepresentativeFieldLocked("repLastName2")}
                  onChange={(event) =>
                    onChange("repLastName2", event.target.value)
                  }
                  placeholder="Opcional"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row form>
            <Col md={3}>
              <FormGroup>
                <Label>
                  Celular <span className="required-mark">*</span>
                </Label>
                <PhoneNumberInput
                  value={form.repCellphone}
                  disabled={isRepresentativeFieldLocked("repCellphone")}
                  onChange={(value) =>
                    onChange("repCellphone", value)
                  }
                  placeholder="campo obligatorio"
                />
                {showInlineError("repCellphone")}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Teléfono</Label>
                <PhoneNumberInput
                  value={form.repPhone}
                  disabled={isRepresentativeFieldLocked("repPhone")}
                  onChange={(value) => onChange("repPhone", value)}
                  placeholder="Ingrese telefono"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Teléfono 2</Label>
                <PhoneNumberInput
                  value={form.repPhone2}
                  disabled={isRepresentativeFieldLocked("repPhone2")}
                  onChange={(value) => onChange("repPhone2", value)}
                  placeholder="Ingrese telefono adicional"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>
                  Correo Electrónico <span className="required-mark">*</span>
                </Label>
                <Input
                  type="email"
                  name="representative_email"
                  autoComplete="section-representative email"
                  invalid={Boolean(inlineErrors.repEmail)}
                  value={form.repEmail}
                  disabled={isRepresentativeFieldLocked("repEmail")}
                  onChange={(event) =>
                    onChange("repEmail", event.target.value)
                  }
                  placeholder="campo obligatorio"
                />
                {showInlineError("repEmail")}
              </FormGroup>
            </Col>
          </Row>
          <Row form>
            <Col md={form.isMedical ? 6 : 12}>
              <FormGroup>
                <Label>Usuario</Label>
                <Input
                  value={legalUsername}
                  readOnly
                  placeholder="Se sugiere al validar datos"
                />
              </FormGroup>
            </Col>
            {form.isMedical && (
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Exequátur <span className="required-mark">*</span>
                  </Label>
                  <Input
                    invalid={Boolean(inlineErrors.physicalExequatur)}
                    value={form.physicalExequatur}
                    disabled={isRepresentativeFieldLocked("physicalExequatur")}
                    onChange={(event) =>
                      onChange(
                        "physicalExequatur",
                        event.target.value,
                      )
                    }
                    placeholder="campo obligatorio"
                  />
                  {showInlineError("physicalExequatur")}
                </FormGroup>
              </Col>
            )}
          </Row>
        </>
      ) : (
        <ValidationFeedback className="mt-2" status="info">
          Debe validar correctamente la cédula o pasaporte del representante legal para habilitar el resto del formulario.
        </ValidationFeedback>
      )}
    </CardBody>
  </Card>
);

export default LegalPersonFields;
